import type { Certificate, SignedDoc, PopupInterface, ClientDoc } from "@/interfaces/interface";
import api from "./api";

class SignKep {
    private docs: any[];
    private popup: PopupInterface;
    private onComplete: (signedDocs: SignedDoc[]) => void;
    private certs: Certificate[];
    private sha: string;
    private showMessage: (msg: string) => void;

    constructor(
        docs: any[], 
        popup: PopupInterface, 
        onComplete: (signedDocs: SignedDoc[]) => void,
        showMessage: (msg: string) => void
    ) {
        this.docs = docs;
        this.popup = popup;
        this.onComplete = onComplete;
        this.showMessage = showMessage;
        this.certs = [];
        this.sha = '';
        console.log('docs',this.docs);
        
        if (!this.docs.length) {
            throw new Error('No docs added');
        }
    }

    public run(): void {
        if (typeof (window as any).cadesplugin === 'undefined') {
            alert('КриптоПро CSP не установлен или недоступен');
            return;
        }

        this.popup.setContent('<div><h6>Получаем сертификаты</h6><div style="display:flex;justify-content:center;"><img width="200px" src="/images/slon_fly_docs.png" /></div></div>');

        (window as any).cadesplugin.then(async () => {
            this.certs = await this.getContainers();
            // Just notify that certificates are ready, let Vue handle rendering
            this.popup.onCertificatesReady?.(this.certs);
        }, (error: any) => {
            console.error(error);
            alert('Ошибка инициализации КриптоПро CSP: ' + error);
        });
    }

    public selectCertificate(thumb: string): void {
        this.sha = thumb;
        this.signDocs();
    }

    public getCertificates(): Certificate[] {
        return this.certs;
    }

    public getSelectedCertificate(): string {
        return this.sha;
    }

    private getContainers(): Promise<Certificate[]> {
        this.showMessage('Инициируем загрузку');
        
        //eslint-disable-next-line
        const self = this;
        return new Promise<Certificate[]>((resolve, reject) => {
            const certs_: Certificate[] = [];
            (window as any).cadesplugin.async_spawn(function* (args: any): Generator<any, void, any> {
                console.log('Start get certs');
                
                try {
                    const oStore = yield (window as any).cadesplugin.CreateObjectAsync("CAdESCOM.Store");
                    yield oStore.Open(
                        (window as any).cadesplugin.CADESCOM_CONTAINER_STORE,
                        (window as any).cadesplugin.CAPICOM_MY_STORE,
                        (window as any).cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED);
                    //eslint-disable-next-line
                    self.showMessage('В процессе...');
                    const oCertificates = yield oStore.Certificates;
                    const count = yield oCertificates.Count;
                    self.showMessage(`Сертификатов найдено: ${count}`);
                    
                    for (let i = 1; i <= count; i++) {
                        self.showMessage(`Получение сертификатов... ${i}`);
                        const cert = yield oCertificates.Item(i);
                        
                        try {
                            const ValidFromDate = new Date((yield cert.ValidFromDate));
                            const ValidToDate = new Date(yield cert.ValidToDate);
                            const isValid = ValidFromDate < new Date() && ValidToDate > new Date();
                            if (!isValid) continue;
                            
                            const cn = yield cert.SubjectName;
                            const innMatch = cn.match(/ИНН(\sЮЛ)?=(?<inn>.+?)(,|$)/);
                            if (!innMatch) continue;
                            
                            const inn = innMatch.groups?.inn;
                            if (!inn) continue;
                            
                            certs_.push({
                                isValid,
                                cn,
                                thumb: yield cert.Thumbprint,
                                validToDate: ValidToDate
                            });
                            
                            self.certs = certs_;
                            // Notify that certificates are updated
                            self.popup.onCertificatesUpdated?.(certs_);
                        } catch (err) {
                            console.error('Error processing certificate:', err);
                            continue;
                        }
                    }
                    
                    self.showMessage('Извлечение сертификатов завершено');
                    resolve(certs_);
                } catch (err) {
                    console.error('Error in getContainers:', err);
                    reject(err);
                }
            }, resolve, reject);
        });
    }

    public setDocs(docs: (ClientDoc | File | Blob)[]): void {
        this.docs = docs;
    }

    private async signDocs(): Promise<void> {
        const oFReader = new FileReader();

        if (typeof (oFReader.readAsDataURL) != "function") {
            alert("Method readAsDataURL() is not supported in FileReader.");
            return;
        }
        
        this.popup.setContent('<div><h6>Подписываем документы</h6><div style="display:flex;justify-content:center;"><img width="200px" src="/images/icons/mascote/slon_work_docs.png" /></div></div>');
        
        const signedDocs: SignedDoc[] = [];
        
        for (const i in this.docs) {
            let doc: any;
            if(!(this.docs[i] instanceof Blob)) {
                doc = await api.getDocByURLFetch(
                    this.docs[i].full_link,
                    this.docs[i].name
                  );
                doc = doc.file;

                // Преобразуем в blob из base64
                const response = await fetch(`data:${this.docs[i].mime};base64,${doc}`);
                doc = await response.blob();
            } else {
                doc = this.docs[i];
            }
            
            oFReader.readAsDataURL(doc);
            
            const sCertSha = this.sha;
            //eslint-disable-next-line
            const self = this;
            
            await new Promise<void>((resolve, reject) => {
                oFReader.onload = (oFREvent) => {
                    (window as any).cadesplugin.async_spawn(function* (): Generator<any, void, any> {
                        try {
                            const header = ";base64,";
                            const sFileData = (oFREvent.target as any).result;
                            const sBase64Data = sFileData.substr(sFileData.indexOf(header) + header.length);

                            if ("" == sCertSha) {
                                alert("Не добавлен отпечаток сертификата");
                                reject(new Error("No certificate thumbprint"));
                                return;
                            }
                            
                            const oStore = yield (window as any).cadesplugin.CreateObjectAsync("CAdESCOM.Store");
                            yield oStore.Open((window as any).cadesplugin.CAPICOM_CURRENT_USER_STORE, (window as any).cadesplugin.CAPICOM_MY_STORE,
                                (window as any).cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED);

                            const oStoreCerts = yield oStore.Certificates;
                            const oCertificates = yield oStoreCerts.Find(
                                (window as any).cadesplugin.CAPICOM_CERTIFICATE_FIND_SHA1_HASH, sCertSha);
                            const certsCount = yield oCertificates.Count;
                            
                            if (certsCount === 0) {
                                alert("Сертификат не найден, либо не установлен в локальное хранилище: " + sCertSha);
                                reject(new Error("Certificate not found"));
                                return;
                            }

                            const oCertificate = yield oCertificates.Item(1);
                            const oSigner = yield (window as any).cadesplugin.CreateObjectAsync("CAdESCOM.CPSigner");
                            yield oSigner.propset_Certificate(oCertificate);
                            yield oSigner.propset_CheckCertificate(true);

                            // Добавим метку времени
                            const oSigningTimeAttr = yield (window as any).cadesplugin.CreateObjectAsync("CADESCOM.CPAttribute");
                            yield oSigningTimeAttr.propset_Name((window as any).cadesplugin.CAPICOM_AUTHENTICATED_ATTRIBUTE_SIGNING_TIME);
                            const oTimeNow = new Date();
                            yield oSigningTimeAttr.propset_Value(oTimeNow);
                            const oAuthAttrs = yield oSigner.AuthenticatedAttributes2;
                            yield oAuthAttrs.Add(oSigningTimeAttr);

                            const oSignedData = yield (window as any).cadesplugin.CreateObjectAsync("CAdESCOM.CadesSignedData");
                            yield oSignedData.propset_ContentEncoding((window as any).cadesplugin.CADESCOM_BASE64_TO_BINARY);
                            yield oSignedData.propset_Content(sBase64Data);
                            
                            let sSignedMessage = '';
                            try {
                                sSignedMessage = yield oSignedData.SignCades(oSigner, (window as any).cadesplugin.CADESCOM_CADES_BES, true);
                            } catch (err) {
                                alert("Failed to create signature. Error: " + (window as any).cadesplugin.getLastError(err));
                                reject(err);
                                return;
                            }

                            yield oStore.Close();

                            // Verify signature
                            const oSignedData2 = yield (window as any).cadesplugin.CreateObjectAsync("CAdESCOM.CadesSignedData");
                            yield oSignedData2.propset_ContentEncoding((window as any).cadesplugin.CADESCOM_BASE64_TO_BINARY);
                            yield oSignedData2.propset_Content(sBase64Data);
                            yield oSignedData2.VerifyCades(sSignedMessage, (window as any).cadesplugin.CADESCOM_CADES_BES, true);
                            
                            console.log("Подпись верифицирована");
                            doc = new File([doc], self.docs[i].name);
                            // Create signature file and add to signedDocs
                            const sigFile = self.createSigFile(sSignedMessage, doc);
                            
                            if (sigFile) {
                                console.log({doc, docInfo: self.docs[i]});
                                signedDocs.push({ doc, sigFile });
                            }
                            
                            resolve();
                        } catch (err) {
                            console.error('Error signing document:', err);
                            reject(err);
                        }
                    });
                };
            });
        }
        
        if (this.onComplete) {
            this.onComplete(signedDocs);
        }
    }

    private createSigFile(content: string, doc: any): File | null {
        try {
            const blob = new Blob([content], { type: 'text/plain' });
            const file = new File([blob], doc.name + '.sig', { type: 'text/plain' });
            return file;
        } catch (error) {
            console.error('Error creating signature file:', error);
            return null;
        }
    }
}

export default SignKep;